const MONEY_UNITS_FACTOR = 100;
const DECIMAL_PLACES = 2;

export interface IMoney {
  equals(other: IMoney): boolean;
  isZero(): boolean;
  isNegative(): boolean;
  abs(): IMoney;
  getValue(): number;
  toUnitsValue(): number;
  toFormattedString(): string;
  toFormattedStringNoDecimals(): string;
  add(other: IMoney): IMoney;
  sub(other: IMoney): IMoney;
  multiply(other: IMoney): IMoney;
  divide(other: IMoney): IMoney;
}

class Money implements IMoney {
  private value: number;

  constructor(value: number) {
    this.value = value || 0;
  }

  equals(other: IMoney): boolean {
    return this.value === other.getValue();
  }

  isZero(): boolean {
    return this.value === 0;
  }

  isNegative(): boolean {
    return this.value < 0;
  }

  abs(): IMoney {
    return new Money(Math.abs(this.value));
  }

  add(other: IMoney): IMoney {
    return new Money(this.getValue() + other.getValue());
  }

  sub(other: IMoney): IMoney {
    return new Money(this.getValue() - other.getValue());
  }

  multiply(other: IMoney): IMoney {
    const unRoundedValue =
      (this.getValue() * MONEY_UNITS_FACTOR * other.getValue() * MONEY_UNITS_FACTOR) /
      (MONEY_UNITS_FACTOR * MONEY_UNITS_FACTOR);
    return new Money(round(unRoundedValue, DECIMAL_PLACES));
  }

  divide(other: IMoney): IMoney {
    const unRoundedValue = (this.getValue() * MONEY_UNITS_FACTOR) / (other.getValue() * MONEY_UNITS_FACTOR);
    return new Money(round(unRoundedValue, DECIMAL_PLACES));
  }

  getValue(): number {
    return this.value;
  }

  toUnitsValue(): number {
    return round(this.value * MONEY_UNITS_FACTOR, 0);
  }

  toFormattedString(): string {
    return this.value.toLocaleString(undefined, {
      maximumFractionDigits: DECIMAL_PLACES,
      minimumFractionDigits: DECIMAL_PLACES
    });
  }

  toFormattedStringNoDecimals(): string {
    return this.value.toLocaleString(undefined, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    });
  }

  toString(): string {
    return this.value.toString();
  }
}

export const money = (money: number | IMoney): IMoney => {
  if (typeof money == 'number') {
    return new Money(money);
  } else {
    return money;
  }
};

export const unitsValueMoney = (unitsValue: number): IMoney => money(unitsValue / MONEY_UNITS_FACTOR);
export const textMoney = (textValue: string): IMoney => money(parseFloat(textValue));
export const unitsTextMoney = (textValue: string): IMoney => money(parseFloat(textValue) / MONEY_UNITS_FACTOR);
export const zeroMoney = (): IMoney => new Money(0);
export const round = (unRoundedValue: number, dps = DECIMAL_PLACES): number => parseFloat(unRoundedValue.toFixed(dps));
